import type { ReactNode } from 'react';
import { cn } from './ui-x';

/** The reference's signature tic: an orange uppercase eyebrow above a title. */
export const Eyebrow = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'text-[11px] font-semibold uppercase tracking-wider text-brand-orange',
      className
    )}
  >
    {children}
  </div>
);

/** House card. Nested boxes step down: 2xl → xl → rounded-lg bg-surface rows. */
export const Card = ({
  children,
  className,
  as: As = 'div',
  ...rest
}: {
  children?: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article';
} & React.HTMLAttributes<HTMLDivElement>) => (
  <As
    className={cn('rounded-2xl border bg-card p-5 shadow-ui-sm', className)}
    {...rest}
  >
    {children}
  </As>
);

export const CardHeader = ({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <div className={cn('flex items-start justify-between gap-4', className)}>
    <div className='min-w-0'>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className='font-display text-base font-bold text-brand-navy'>{title}</h3>
      {description && (
        <p className='mt-1 text-sm text-muted-foreground'>{description}</p>
      )}
    </div>
    {actions && <div className='flex shrink-0 items-center gap-2'>{actions}</div>}
  </div>
);

/** An inner row inside a white card. */
export const CardRow = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn('rounded-lg bg-surface px-3 py-2', className)}>{children}</div>
);

export default Card;
